import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  Tabs,
  Tab,
  Divider,
  Alert,
  Slide,
  Paper,
  Avatar,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import StripeCheckout from '../components/Payment/StripeCheckout';
import PayPalCheckout from '../components/Payment/PayPalCheckout';
import ClicToPayCheckout from '../components/Payment/ClicToPayCheckout';
import { apiClient } from '../utils/apiClient';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';

/* ─── Preset amounts (TND) ─────────────────────────────────── */
const PRESETS = [10, 25, 50, 100, 200, 500];

/* ─── Gateway tab config ───────────────────────────────────── */
const ALL_GATEWAYS = [
  { id: 'clictopay', label: 'ClicToPay', icon: <CreditCardIcon /> },
  { id: 'stripe', label: 'Stripe', icon: <CreditCardIcon /> },
  { id: 'paypal', label: 'PayPal', icon: <PaymentIcon /> },
];

export default function WalletTopUp() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [amount, setAmount] = useState(50);
  const [customAmt, setCustomAmt] = useState('');
  // Only offer gateways the server actually has configured. Previously both
  // tabs rendered unconditionally, so with no credentials set every payment
  // attempt failed with an opaque error.
  const [gateways, setGateways] = useState(null); // null = still loading
  const [gateway, setGateway] = useState(0);      // index into `gateways`
  const [success, setSuccess] = useState(null); // { amountTND }
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/api/payment-settings/public')
      .then((res) => {
        if (cancelled) return;
        const enabled = ALL_GATEWAYS.filter((g) => res?.[g.id]?.enabled);
        setGateways(enabled);
        setGateway(0);
      })
      .catch(() => { if (!cancelled) setGateways([]); });
    return () => { cancelled = true; };
  }, []);

  const effectiveAmount = customAmt !== '' ? Number(customAmt) : amount;
  const activeGateway = gateways && gateways[gateway];

  const handlePreset = (val) => {
    setAmount(val);
    setCustomAmt('');
  };

  const handleCustom = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d+(\.\d{0,3})?$/.test(val)) {
      setCustomAmt(val);
    }
  };

  const handleSuccess = async ({ amountTND }) => {
    // Capture the expected new balance now, before refreshUser, so the success
    // screen shows the correct value even if refreshUser fails.
    const expectedNewBalance = currentBalance + Number(amountTND);
    setSuccess({ amountTND, expectedNewBalance });
    setErrorMsg('');
    try {
      await refreshUser(); // update wallet balance in AuthContext
    } catch (_) {
      // non-critical — UI still shows expectedNewBalance
    }
  };

  const handleError = (msg) => {
    setErrorMsg(msg);
  };

  const currentBalance = user?.wallet?.tokenBalance ?? 0;

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Slide in direction="up">
          <Paper
            elevation={0}
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              borderRadius: '25px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'success.light',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
            </Avatar>

            <Typography variant="h4" fontWeight={800} gutterBottom>
              Top-Up Successful!
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {success.amountTND} TND has been added to your wallet.
            </Typography>

            <Box
              sx={{
                mt: 3,
                mb: 4,
                p: 2.5,
                borderRadius: '16px',
                bgcolor: 'success.lighter' || 'rgba(0,196,140,0.08)',
                display: 'inline-block',
                minWidth: 220,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                New Balance
              </Typography>
              <Typography variant="h4" fontWeight={800} color="success.main">
                {(success.expectedNewBalance ?? currentBalance).toFixed(3)} TND
              </Typography>
            </Box>

            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                sx={{ borderRadius: '25px', px: 4 }}
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outlined"
                sx={{ borderRadius: '25px', px: 4 }}
                onClick={() => {
                  setSuccess(null);
                  setErrorMsg('');
                }}
              >
                Top Up Again
              </Button>
            </Box>
          </Paper>
        </Slide>
      </Container>
    );
  }

  return (
    <SciFiBackground>
      <Navbar />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ borderRadius: '25px', minWidth: 0 }}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={800}>
            Top Up Wallet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add funds to your Vitria wallet
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* ── Left: amount + gateway selection ──────────────── */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: '25px', border: '1px solid', borderColor: 'divider' }}
          >
            {/* Current balance */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: '16px',
                bgcolor: 'primary.main',
                color: '#fff',
                mb: 3,
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Current Balance
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  {currentBalance.toFixed(3)} TND
                </Typography>
              </Box>
            </Box>

            {/* Preset amounts */}
            <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
              Select Amount (TND)
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1.5} mb={2}>
              {PRESETS.map((val) => (
                <Chip
                  key={val}
                  label={`${val} TND`}
                  onClick={() => handlePreset(val)}
                  variant={amount === val && customAmt === '' ? 'filled' : 'outlined'}
                  color={amount === val && customAmt === '' ? 'primary' : 'default'}
                  sx={{ borderRadius: '25px', fontWeight: 700, fontSize: '0.9rem', py: 2.5, px: 1 }}
                />
              ))}
            </Box>

            <TextField
              label="Custom Amount (TND)"
              value={customAmt}
              onChange={handleCustom}
              fullWidth
              size="small"
              type="text"
              inputMode="decimal"
              placeholder="e.g. 75"
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <Divider sx={{ mb: 3 }} />

            {/* Gateway tabs */}
            <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
              Payment Method
            </Typography>
            <Tabs
              value={gateway}
              onChange={(_, v) => { setGateway(v); setErrorMsg(''); }}
              sx={{
                mb: 3,
                '& .MuiTabs-indicator': { height: 3, borderRadius: 2 },
                '& .MuiTab-root': { borderRadius: '12px', fontWeight: 700 },
              }}
            >
              {(gateways || []).map((g) => (
                <Tab key={g.id} icon={g.icon} label={g.label} iconPosition="start" />
              ))}
            </Tabs>

            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setErrorMsg('')}>
                {errorMsg}
              </Alert>
            )}

            {/* Checkout component */}
            {gateways === null ? (
              <Alert severity="info" sx={{ borderRadius: '12px' }}>
                Loading payment methods…
              </Alert>
            ) : gateways.length === 0 ? (
              <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                Wallet top-up is temporarily unavailable — no payment method is
                configured yet. Please try again later or contact support.
              </Alert>
            ) : effectiveAmount >= 1 ? (
              <>
                {activeGateway?.id === 'clictopay' && (
                  <ClicToPayCheckout
                    amountTND={effectiveAmount}
                    onError={handleError}
                  />
                )}
                {activeGateway?.id === 'stripe' && (
                  <StripeCheckout
                    amountTND={effectiveAmount}
                    onSuccess={handleSuccess}
                    onError={handleError}
                  />
                )}
                {activeGateway?.id === 'paypal' && (
                  <PayPalCheckout
                    amountTND={effectiveAmount}
                    onSuccess={handleSuccess}
                    onError={handleError}
                  />
                )}
              </>
            ) : (
              <Alert severity="info" sx={{ borderRadius: '12px' }}>
                Please enter an amount of at least 1 TND.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* ── Right: info panel ──────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: '25px', border: '1px solid', borderColor: 'divider', height: '100%' }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Summary
            </Typography>

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Top-up amount</Typography>
              <Typography fontWeight={700}>
                {effectiveAmount >= 1 ? `${effectiveAmount} TND` : '—'}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Gateway</Typography>
              <Typography fontWeight={700}>{activeGateway?.label || "—"}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Current balance</Typography>
              <Typography fontWeight={700}>{currentBalance.toFixed(3)} TND</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={700}>New balance</Typography>
              <Typography fontWeight={800} color="success.main" fontSize="1.1rem">
                {effectiveAmount >= 1
                  ? (currentBalance + effectiveAmount).toFixed(3)
                  : currentBalance.toFixed(3)}{' '}
                TND
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              • Funds are added instantly after payment confirmation.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              • Payments are processed securely by the selected provider.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              • Contact support if a charge appears but your balance wasn't updated.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      </Container>
    </SciFiBackground>
  );
}
